import { useCallback, useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { useGetUserRoles, useUpdateUserRoles, useGetRoles, type RoleDto, type RoleNameDto } from '@dhl/mdp-api'
import { Box, Button, Card, Checkbox, H6, IconButton, List, ListItem, ListItemText } from '@dhl-ui/components'
import { EditSolidIcon } from '@dhl-ui/icons'
import { useNotificationMessage, sortByName, camelCaseToSpaces } from '~/common'
import type { User } from '~/modules/users/model'

type Props = {
  user: User
  isEditingRoles: boolean
  onSaveOrCancel: () => void
  onEditButtonClick: () => void
}

export const RolesForm = ({ user, onSaveOrCancel, onEditButtonClick, isEditingRoles }: Props) => {
  const { id, userType } = user
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()

  const [formData, setFormData] = useState<RoleNameDto[]>([])

  const maybeShowError = (error: any) => {
    if (isAxiosError(error)) {
      showError(error.message)
    }
  }

  const { data: mappedRoles, error: rolesError } = useGetUserRoles(id)
  const { data: availableRoles, error: availableRolesError } = useGetRoles({ userType })
  const { mutateAsync: updateRoles, isPending, isError, isSuccess, error } = useUpdateUserRoles()

  useEffect(() => {
    maybeShowError(availableRolesError)
  }, [availableRolesError])

  useEffect(() => {
    maybeShowError(rolesError)
  }, [rolesError])

  const handleRoleCheck = ({ name }: RoleDto) => {
    const roleName = name
    if (formData.includes(roleName)) {
      setFormData((data) => data.filter((item) => item !== roleName))
    } else {
      setFormData((data) => data.concat(roleName))
    }
  }

  const saveRoles = useCallback(async () => {
    await updateRoles({ emailOrId: id, data: formData as unknown as RoleNameDto[] })
    onSaveOrCancel()
  }, [id, formData, onSaveOrCancel, updateRoles])

  useEffect(() => {
    setFormData(mappedRoles ?? [])
  }, [mappedRoles])

  useEffect(() => {
    if (isError) {
      maybeShowError(error)
    }
    if (isSuccess) {
      showSuccess(t('admin.usersPage.rolesView.savedSuccess'))
    }
  }, [isError, error, isSuccess, t])

  return (
    <Card data-testid="roles-form-card">
      <Card.Header
        title={t('admin.usersPage.rolesView.titleText')}
        endNode={
          !isEditingRoles ? (
            <IconButton aria-label="edit roles" onClick={onEditButtonClick}>
              <EditSolidIcon />
            </IconButton>
          ) : undefined
        }
      />
      <Card.Content>
        <List gap={isEditingRoles ? 0 : 2}>
          {availableRoles?.sort(sortByName<RoleDto>).map((role) => {
            return isEditingRoles ? (
              <ListItem isButton onClick={() => handleRoleCheck(role)} hasDivider={false}>
                <Checkbox
                  edge="start"
                  // `role.name` *should* be a Role, but the API returns it as a string. That will be fixed.
                  checked={formData?.includes(role.name)}
                  name="roles"
                />
                <ListItemText
                  secondary={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        ml: 2,
                      }}
                    >
                      <H6>{camelCaseToSpaces(role.name)}</H6>
                      <Box sx={{ textAlign: 'left', alignSelf: 'flex-start', width: '100%' }}>
                        {t(`admin.roles.${role.name}.description`)}
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ) : (
              formData?.includes(role.name) && (
                <ListItemText
                  secondary={<H6>{camelCaseToSpaces(role.name)}</H6>}
                  tertiary={t(`admin.roles.${role.name}.description`)}
                />
              )
            )
          })}
        </List>

        {isEditingRoles ? (
          <Box marginTop={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button
              data-testid="click-cancel"
              size="medium"
              variant="outlined"
              color="primary"
              onClick={onSaveOrCancel}
              disabled={isPending}
            >
              {t('admin.usersPage.rolesView.buttonCancel')}
            </Button>
            <Button
              data-testid="click-save"
              size="medium"
              color="primary"
              variant="contained"
              onClick={saveRoles}
              disabled={isPending}
            >
              {t('admin.usersPage.rolesView.buttonSave')}
            </Button>
          </Box>
        ) : null}
      </Card.Content>
    </Card>
  )
}
